import { AlertTriangle, X } from 'lucide-react';
import type { ActivitySnapshot } from '@volley/crucible-ipc-types/ipc-channels';
import { useActivityPrefs } from '../hooks/useActivityPrefs';
import { useFeatureFlags } from '../hooks/useFeatureFlags';

/**
 * Amber warning band above the activity banner for games whose
 * `crucible.json.templateVersion` is below the required threshold
 * (Crucible #602 / #670, design plan §7).
 *
 * Visibility is DERIVED from two signals, not pushed:
 *   1. `snapshot.templateCompatible === false` — carried on every
 *      `activity:state` push, so a Workspace reload still sees the
 *      flag without depending on the once-per-(gameId, sessionId)
 *      `activity:template-warning` push.
 *   2. `useActivityPrefs(gameId).data?.templateWarningDismissed`
 *      being falsy — read from the C1 `activity-prefs:load` sidecar
 *      so the dismissal persists across Foundry restarts.
 *
 * Either signal flipping false → true causes immediate unmount.
 * The `activity:template-warning` push channel is intentionally NOT
 * a gating signal here — it is retained for analytics only.
 *
 * `templateCompatible: true` also unmounts unconditionally. The C1
 * registry clears the persisted dismissal when it observes the
 * template version has caught up (design §7 "Subtle edge case"),
 * so a future incompatible game on the same `gameId` re-shows the
 * banner without extra renderer work.
 *
 * Per Codex round-1 HIGH on the C2 design, we hold the banner OFF
 * while the prefs load is in flight (`data === undefined`). Without
 * this, the banner would flash visible on first paint and then
 * disappear once the persisted `dismissed: true` arrived from disk
 * — a UX glitch every Workspace mount for a user who had already
 * dismissed.
 *
 * Mount point: this component renders ABOVE `<ActivityBanner>` in
 * the centre-column shell. The two banners are mutually exclusive
 * (design §7 "Mutual exclusivity") — `ActivityBanner`'s mount
 * predicate already short-circuits on `templateCompatible === false`,
 * so when this banner is visible the activity band is invisible.
 */
export function TemplateTooOldBanner({
  gameId,
  snapshot,
}: {
  gameId: string;
  snapshot: ActivitySnapshot | null;
}): React.JSX.Element | null {
  const flags = useFeatureFlags();
  // Gate the prefs IPC on the flag too so a user running with
  // `CRUCIBLE_AGENT_TRANSPARENCY=0` (the opt-OUT kill switch since
  // Crucible #673 / C2 ship) never even hits the sidecar load.
  // The hook still has to be invoked on every render to satisfy
  // the Rules of Hooks; the `enabled` toggle is the
  // production-equivalent of a no-op.
  const flagOn = flags.loaded && flags.agentTransparency;
  const { data, setTemplateWarningDismissed } = useActivityPrefs(gameId, {
    enabled: flagOn,
  });

  // 0. Feature-flag gate (CRUCIBLE_AGENT_TRANSPARENCY). Defence in
  //    depth — the centre-column shell (#671) also gates the mount,
  //    but the component MUST be safe if a future caller mounts it
  //    without the parent gate. `!flags.loaded` covers the pre-IPC
  //    window: until the renderer has read the flag, we treat
  //    transparency as off.
  if (!flagOn) return null;

  // 1. No snapshot yet (registry not initialised / no build in
  //    flight) — nothing to warn about.
  if (snapshot === null) return null;

  // 2. Template is compatible — no warning needed.
  if (snapshot.templateCompatible !== false) return null;

  // 3. Prefs still loading — hold off to avoid a flash on remount
  //    when the user has previously dismissed (data === undefined
  //    on first paint after mount). `null` is a valid loaded
  //    state (no sidecar yet) and falls through to render.
  if (data === undefined) return null;

  // 4. User has already dismissed — stay unmounted.
  if (data !== null && data.templateWarningDismissed) return null;

  const onDismiss = (): void => {
    // The optimistic cache write inside
    // `setTemplateWarningDismissed` unmounts the banner on the
    // same render, so we don't await the IPC. Failures behind
    // the optimistic update have nowhere to surface in the
    // current UI (no toast system) and the next mount-driven
    // refetch will reconcile with disk — so we swallow the
    // rejection deliberately rather than leaving it unhandled.
    // Logged at warn level so a real outage surfaces in the
    // DevTools console.
    setTemplateWarningDismissed(true).catch((err: unknown) => {
      console.warn(
        'TemplateTooOldBanner: activity-prefs:save rejected — dismissal will not persist past next refetch',
        err,
      );
    });
  };

  return (
    <section
      role="region"
      aria-label="Template version notice"
      data-testid="template-too-old-banner"
      className="flex items-center gap-3 border-b border-amber-400/20 bg-amber-500/10 px-4 py-2"
    >
      <AlertTriangle className="h-4 w-4 flex-none text-amber-300" aria-hidden="true" />
      <span className="flex-1 text-sm text-amber-100">
        Template too old for live activity — run{' '}
        <code className="rounded bg-amber-500/15 px-1 py-0.5 font-mono text-xs text-amber-50">
          crucible update
        </code>{' '}
        for the full experience.
      </span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss template warning"
        data-testid="template-too-old-banner-dismiss"
        className="flex-none rounded-md p-1 text-amber-200/70 hover:bg-amber-500/10 hover:text-amber-100"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </section>
  );
}
